import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { AudioLines, History, Mic, Settings, Menu, LogOut } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";

export const openVoiceKeyboard = () => window.dispatchEvent(new Event("mulankan:open-kb"));

const TABS = [
  { to: "/flow", label: "Flow", icon: AudioLines },
  { to: "/history", label: "History", icon: History },
  null,
  { to: "/settings", label: "Settings", icon: Settings },
];

const tabCls = ({ isActive }) =>
  `flex flex-col items-center justify-center gap-1 flex-1 h-full text-[10.5px] transition-colors ${isActive ? "text-[var(--accent)]" : "text-white/50"}`;

function MoreSheet({ open, setOpen, nav, email, onLogout }) {
  const extra = nav.filter((l) => !TABS.some((t) => t?.to === l.to));
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="bg-[#14110E] border-white/10 text-[var(--ink)] rounded-t-3xl pb-[max(1.5rem,env(safe-area-inset-bottom))]" data-testid="mobile-more-sheet">
        <SheetTitle className="font-serif text-2xl text-[var(--ink)]">More</SheetTitle>
        <SheetDescription className="text-xs text-white/40 truncate" data-testid="mobile-more-email">{email}</SheetDescription>
        <div className="grid grid-cols-3 gap-2 mt-5">
          {extra.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${l.to.slice(1)}`}
              className={({ isActive }) => `flex flex-col items-center gap-2 rounded-2xl py-4 text-xs border transition-colors ${isActive ? "border-[var(--accent)]/60 text-[var(--ink)] bg-[var(--accent)]/10" : "border-white/[0.06] text-white/60 bg-white/[0.02]"}`}>
              <l.icon size={20} /> {l.label}
            </NavLink>
          ))}
        </div>
        <button onClick={onLogout} data-testid="mobile-logout-button" className="mt-5 w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-sm text-rose-300 border border-white/[0.06]">
          <LogOut size={16} /> Log out
        </button>
      </SheetContent>
    </Sheet>
  );
}

export function MobileTabBar({ nav, email, onLogout }) {
  const [more, setMore] = useState(false);
  // Keep a focused field clear of the bar once the phone keyboard has opened.
  useEffect(() => {
    const on = (e) => {
      const el = e.target;
      if (!/INPUT|TEXTAREA/.test(el.tagName) || window.innerWidth >= 768) return;
      setTimeout(() => {
        const vh = window.visualViewport?.height || window.innerHeight;
        if (el.getBoundingClientRect().bottom > vh - 88) el.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 300);
    };
    document.addEventListener("focusin", on);
    return () => document.removeEventListener("focusin", on);
  }, []);
  return (
    <>
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-[#100D0B]/95 backdrop-blur-xl border-t border-white/[0.07]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-tab-bar">
        <div className="flex items-stretch h-16">
          {TABS.map((t) => t ? (
            <NavLink key={t.to} to={t.to} className={tabCls} data-testid={`mobile-tab-${t.to.slice(1)}`}><t.icon size={20} /> {t.label}</NavLink>
          ) : (
            <div key="voice" className="flex-1 flex items-center justify-center">
              <button onClick={openVoiceKeyboard} aria-label="Voice keyboard" data-testid="mobile-tab-voice"
                className="-mt-7 w-14 h-14 rounded-full bg-[var(--accent)] text-black flex items-center justify-center shadow-[0_8px_30px_rgba(255,138,61,0.45)] active:scale-95 transition-transform">
                <Mic size={24} />
              </button>
            </div>
          ))}
          <button onClick={() => setMore(true)} className="flex flex-col items-center justify-center gap-1 flex-1 text-[10.5px] text-white/50" data-testid="mobile-tab-more"><Menu size={20} /> More</button>
        </div>
      </nav>
      <MoreSheet open={more} setOpen={setMore} nav={nav} email={email} onLogout={onLogout} />
    </>
  );
}
